import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Building2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { partnerApi } from '../services/partner-api';
import { handleApiError } from '../lib/api-client';
import { CopyButton } from '../components/common/CopyButton';
import { EntityBadge } from '../components/common/EntityBadge';
import { QRCodeCard } from '../components/qr/QRCodeCard';
import type { PartnerBusinessDetail as BusinessDetailType } from '../types';

export function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<BusinessDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    partnerApi.getBusiness(id)
      .then(setBusiness)
      .catch((err) => setError(handleApiError(err)))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={16} /> Powrot do wyszukiwarki
        </Link>
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error || 'Nie znaleziono obiektu'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
        <ArrowLeft size={16} /> Powrot do wyszukiwarki
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <EntityBadge type="business" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{business.displayName}</h1>
                {business.companyName && (
                  <p className="text-gray-500 mt-1">{business.companyName}</p>
                )}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Dane</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="text-gray-500">Typ</dt>
                <dd className="font-medium text-gray-900">Obiekt sportowy</dd>
              </div>
              {business.nip && (
                <div>
                  <dt className="text-gray-500">NIP</dt>
                  <dd className="font-medium text-gray-900">{business.nip}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Miasto</dt>
                <dd className="font-medium text-gray-900 flex items-center gap-1">
                  <MapPin size={14} /> {business.city}
                </dd>
              </div>
              {business.address && (
                <div>
                  <dt className="text-gray-500">Adres</dt>
                  <dd className="font-medium text-gray-900">
                    {business.address}{business.postalCode ? `, ${business.postalCode}` : ''}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">TPay</dt>
                <dd className="font-medium flex items-center gap-1">
                  {business.hasTPay ? (
                    <><CheckCircle size={14} className="text-emerald-500" /> Skonfigurowany</>
                  ) : (
                    <><XCircle size={14} className="text-gray-400" /> Nieskonfigurowany</>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">ID Spotto</dt>
                <dd className="font-mono text-xs text-gray-600 break-all">{business.id}</dd>
              </div>
            </dl>
          </div>

          {/* Deep Link */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-3">Deep Link (POS)</h2>
            <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm text-gray-700 break-all">
              {business.deepLink}
            </div>
            <div className="mt-3">
              <CopyButton text={business.deepLink} label="Kopiuj link" />
            </div>
          </div>

          {/* Facilities */}
          {business.facilities.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">
                Obiekty ({business.facilities.length})
              </h2>
              <ul className="space-y-2">
                {business.facilities.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-sm">
                    <Building2 size={14} className="text-gray-400" />
                    <span className="text-gray-900">{f.name}</span>
                    {f.type && (
                      <span className="text-gray-400">({f.type})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* QR Code sidebar */}
        <div>
          <QRCodeCard
            url={business.deepLink}
            entityName={business.displayName}
            entityType="business"
            entityId={business.id}
          />
        </div>
      </div>
    </div>
  );
}
